/**
 * SmarterElements
 * Main entry point for embeddable UI components
 * Using route-based iframe approach
 */

import { createSmarterElements, SmarterElements as CoreSmarterElements, ElementInstance } from './core';

// Extend the Window interface to include SmarterElements
declare global {
  interface Window {
    SmarterElements: SmarterElements;
  }
}

// Define the public interface for SmarterElements
type SmarterElements = {
  (type: string, options?: Record<string, unknown>): ElementInstance;
  create: (type: string, options?: Record<string, unknown>) => ElementInstance;
};

/**
 * SmarterElements factory function
 * Creates and returns a configured SmarterElements instance
 * @param options - Configuration options
 * @returns Configured instance with all elements registered
 */
const SmarterElementsFactory = function(options: Record<string, unknown> = {}): SmarterElements {
    // Create a new core instance
  const instance: CoreSmarterElements = createSmarterElements(options);

  // Create the factory function with proper typing
  const factory = ((type: string, elementOptions: Record<string, unknown> = {}): ElementInstance => {
    return instance.create(type, elementOptions);
  }) as SmarterElements;

  // Add the create method
  factory.create = (type: string, elementOptions: Record<string, unknown> = {}): ElementInstance => {
    return instance.create(type, elementOptions);
  };

  // Attach the instance to the factory function for advanced usage
  Object.defineProperty(factory, 'instance', {
    value: instance,
    writable: false,
    enumerable: false,
    configurable: false,
  });

  return factory;
}

// For UMD/IIFE builds, expose SmarterElements as a callable function
if (typeof window !== 'undefined') {
  // Create the main function that will be assigned to window.SmarterElements
  const SmarterElementsMain = function(options: Record<string, unknown> = {}) {
    // Create and return an instance of the factory
    return SmarterElementsFactory(options);
  };
  
  // Add static methods
  SmarterElementsMain.init = function(options: Record<string, unknown> = {}) {
    return SmarterElementsFactory(options);
  };
  
  SmarterElementsMain.create = function(type: string, options: Record<string, unknown> = {}) {
    return SmarterElementsFactory({}).create(type, options);
  };
  
  // For backward compatibility, expose the factory as a static property
  SmarterElementsMain.SmarterElements = SmarterElementsFactory;
  
  // Assign to window.SmarterElements
  window.SmarterElements = SmarterElementsMain as unknown as SmarterElements;
  
  // For debugging
  console.debug('SmarterElements exposed on window:', window.SmarterElements);
}

// Export components for direct usage in React applications
export { createSmarterElements, SmarterElements, ElementInstance } from './core';

// Export modal components and hooks
export { default as ElementModal } from './components/ElementModal';
export {
  default as ElementModalProvider,
  useElementModal,
} from './components/ElementModalProvider';

// Export the factory function as default
export default SmarterElementsFactory;
